import * as React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Pagination,
  Box,
  Stack,
  IconButton,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Button } from 'react-admin';
import TelegramIcon from '@mui/icons-material/Telegram';
import CallIcon from '@mui/icons-material/Call';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

type Order = 'asc' | 'desc';

export default function BroadcastList() {
  const navigate = useNavigate();
  const [data, setData] = React.useState<any[]>([]);
  const [page, setPage] = React.useState(1);
  const [pages, setPages] = React.useState(1);
  const [orderBy, setOrderBy] = React.useState('createdAt');
  const [order, setOrder] = React.useState<Order>('desc');

  React.useEffect(() => {
    fetch(
      `${API_URL}/broadcasts?page=${page}&perPage=10&sortField=${orderBy}&sortOrder=${order.toUpperCase()}`,
    )
      .then((r) => r.json())
      .then((r) => {
        setData(r.data);
        setPages(r.pages);
      });
  }, [page, orderBy, order]);

  const handleSort = (field: string) => {
    setOrderBy(field);
    setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const formatDateTime = (value: string) => {
    const d = new Date(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const renderChannelIcons = (channelType: string) => {
    switch (channelType) {
      case 'viber':
        return <CallIcon color="primary" />;
      case 'telegram':
        return <TelegramIcon color="primary" />;
      case 'all':
        return (
          <Stack direction="row" spacing={0.5}>
            <CallIcon color="primary" />
            <TelegramIcon color="primary" />
          </Stack>
        );
      default:
        return null;
    }
  };

  const renderAuditory = (auditory: string) => {
    switch (auditory) {
      case 'new':
        return 'Нова';
      case 'bot':
        return 'Аудиторія бота';
      case 'all':
        return 'Всі';
      default:
        return auditory;
    }
  };

  const renderList = (items: string[]) => {
    if (!items.length) return '—';

    const limit = 10;

    if (items.length <= limit) {
      return items.join(', ');
    }

    const visible = items.slice(0, limit).join(', ');
    return `${visible} та інші...`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>Розсилки</h2>
        <Button
          variant="contained"
          onClick={() => navigate('/broadcasts/new')}
          sx={{ '& .MuiButton-startIcon': { margin: 0 }, justifyContent: 'center' }}
        >
          Додати розсилку
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'message'}
                  direction={order}
                  onClick={() => handleSort('message')}
                >
                  Повідомлення
                </TableSortLabel>
              </TableCell>

              <TableCell>Картинка</TableCell>
              <TableCell>Області</TableCell>
              <TableCell>Міста</TableCell>
              <TableCell>Канал</TableCell>
              <TableCell>Аудиторія</TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === 'scheduledDateAt'}
                  direction={order}
                  onClick={() => handleSort('scheduledDateAt')}
                >
                  Заплановано
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === 'isSended'}
                  direction={order}
                  onClick={() => handleSort('isSended')}
                >
                  Статус
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={order}
                  onClick={() => handleSort('createdAt')}
                >
                  Створено
                </TableSortLabel>
              </TableCell>

              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row) => {
              const regions: string[] = (row.broadcastRegions ?? []).map((r: any) => r.regionName);
              const cities: string[] = (row.broadcastCities ?? []).map((c: any) => c.name);

              return (
                <TableRow key={row.broadcastId}>
                  <TableCell>
                    <Box
                      component="span"
                      sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      dangerouslySetInnerHTML={{ __html: row.message }}
                    />
                  </TableCell>

                  <TableCell>
                    {row.broadcastMedia?.imageUrl && (
                      <img src={`${IMAGE_URL}/${row.broadcastMedia.imageUrl}`} width={60} />
                    )}
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row?.geoAll ? 'Всі' : renderList(regions)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row?.geoAll ? 'Всі' : renderList(cities)}
                    </Typography>
                  </TableCell>

                  <TableCell>{renderChannelIcons(row.channelType)}</TableCell>
                  <TableCell>{renderAuditory(row.auditory)}</TableCell>

                  <TableCell>
                    {row.scheduledDateAt} {row.scheduledTimeAt}
                  </TableCell>

                  <TableCell>
                    {row.isSended ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <ScheduleIcon color="warning" />
                    )}
                  </TableCell>

                  <TableCell sx={{ color: 'text.secondary' }}>
                    {formatDateTime(row.createdAt)}
                  </TableCell>

                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/broadcast/${row.broadcastId}`)}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        color="error"
                        onClick={() => {
                          if (!confirm('Видалити розсилку?')) return;
                          fetch(`${API_URL}/broadcast/${row.broadcastId}`, { method: 'DELETE' }).then(
                            () => setData((prev) => prev.filter((b) => b.broadcastId !== row.broadcastId)),
                          );
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination page={page} count={pages} onChange={(_, p) => setPage(p)} />
      </Box>
    </Box>
  );
}